import { getUsers } from '@Actions/users';
import { ActionButton } from '@Components/Buttons';
import {
	ActionBar,
	ActionBarItemGroup,
	DropdownSelect,
	InnovyzeRole,
	rem,
	StylovyzeSearchBar,
} from '@innovyze/stylovyze';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import {
	ALL_ENTITLEMENTS,
	ALL_ROLES,
	UserParams,
	UserTableSelection,
} from '@Types';
import { subscriptionsList } from '@Types/entitlements.types';
import { addRouteNamespace } from '@Utils/actions';
import React, { ChangeEvent, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { EntitlementsTableHeaderWrapper } from './EntitlementsTable.styles';

interface Props {
	userTableSelection: UserTableSelection;
	setUserTableSelection: React.Dispatch<
		React.SetStateAction<UserTableSelection>
	>;
	createGetUsersRequest: (
		userTableSelectionState: UserTableSelection,
	) => UserParams;
}

export default function EntitlementsTableHeader({
	setUserTableSelection,
	userTableSelection,
	createGetUsersRequest,
}: Props): React.ReactElement {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const products = useSelector(getValue).products.list;
	const productsString = useMemo(
		() => products.map(el => el.name),
		[products],
	);

	const roleFilters = [
		{ key: ALL_ROLES, value: ALL_ROLES, label: t('All Roles') },
		{
			key: InnovyzeRole.READ_ONLY,
			value: InnovyzeRole.READ_ONLY,
			label: t('User'),
		},
		{
			key: InnovyzeRole.READ_WRITE,
			value: InnovyzeRole.READ_WRITE,
			label: t('Editor'),
		},
		{
			key: InnovyzeRole.ADMIN,
			value: InnovyzeRole.ADMIN,
			label: t('Admin'),
		},
	];

	const handleRoleChange = (event: ChangeEvent<{ value: unknown }>) => {
		const selectedFilter = event.target.value as
			| typeof ALL_ROLES
			| InnovyzeRole;
		const tableSelection = { ...userTableSelection, role: selectedFilter };
		setUserTableSelection(tableSelection);
		const getUsersRequest = createGetUsersRequest(tableSelection);
		dispatch(getUsers(getUsersRequest));
	};
	const entitlementFilters = [
		{
			key: ALL_ENTITLEMENTS,
			value: ALL_ENTITLEMENTS,
			label: t('All Subscriptions'),
		},
		...subscriptionsList(t)
			.filter(el => productsString.includes(el.key))
			.map(item => item.value),
	];
	const handleEntitlementChange = (
		event: ChangeEvent<{ value: unknown }>,
	) => {
		const selectedFilter = event.target.value as
			| typeof ALL_ENTITLEMENTS
			| string;

		const tableSelection = {
			...userTableSelection,
			entitlement: selectedFilter,
		};
		setUserTableSelection(tableSelection);
		const getUsersRequest = createGetUsersRequest(tableSelection);
		dispatch(getUsers(getUsersRequest));
	};
	return (
		<ActionBar>
			<EntitlementsTableHeaderWrapper>
				<StylovyzeSearchBar
					handleSearchSubmit={(search: string) => {
						const getUsersRequest = createGetUsersRequest({
							...userTableSelection,
							search,
						});
						setUserTableSelection({
							...userTableSelection,
							search,
						});
						dispatch(getUsers(getUsersRequest));
					}}
					placeholder={t('Search User')}
					dataCy={'entitlementsTableSearchBar'}
				/>
				<DropdownSelect
					dataCy="roleFilter"
					currentSelection={userTableSelection.role}
					title={t('Role')}
					options={roleFilters}
					onChange={handleRoleChange}
				/>
				<DropdownSelect
					dataCy="entitlementFilter"
					currentSelection={userTableSelection.entitlement}
					title={t('Subscriptions')}
					options={entitlementFilters}
					onChange={handleEntitlementChange}
				/>
			</EntitlementsTableHeaderWrapper>
			<ActionBarItemGroup elementsWidth={rem(130)}>
				<ActionButton
					data-cy="createUser"
					color="primary"
					style={{ maxWidth: '100%' }}
					onClick={() => {
						history.push(addRouteNamespace('create-user'));
					}}>
					{t('Create User')}
				</ActionButton>
			</ActionBarItemGroup>
		</ActionBar>
	);
}
